import React from 'react';
import { Button } from '../../BasicComponents/Button';
import type { ErrorBoundaryProps, ErrorBoundaryState } from './types';

// ─── Default fallback UI ──────────────────────────────────────────────────────

function ErrorIllustration() {
  return (
    <svg
      className="h-[120px] w-[120px] text-neutral-300 dark:text-neutral-600"
      viewBox="0 0 120 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="60" cy="60" r="50" stroke="currentColor" strokeWidth="4" />
      <path
        d="M60 38v28"
        stroke="currentColor"
        strokeWidth="5"
        strokeLinecap="round"
      />
      <circle cx="60" cy="80" r="3.5" fill="currentColor" />
    </svg>
  );
}

interface DefaultFallbackProps {
  error: Error | null;
  onReset: () => void;
}

function DefaultFallback({ error, onReset }: DefaultFallbackProps) {
  return (
    <div
      className="flex flex-1 flex-col items-center justify-center py-16 px-4 min-h-[320px] text-center"
      tabIndex={-1}
    >
      <ErrorIllustration />

      <h2 className="mt-4 text-base font-semibold text-neutral-900 dark:text-neutral-100">
        Something went wrong
      </h2>

      {error?.message && (
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400 max-w-[360px]">
          {error.message}
        </p>
      )}

      <div className="mt-6 flex items-center gap-3">
        <Button variant="primary" onClick={onReset}>
          Try Again
        </Button>
        <Button variant="secondary" onClick={() => (window.location.href = '/')}>
          Go Home
        </Button>
      </div>
    </div>
  );
}

// ─── ErrorBoundary ────────────────────────────────────────────────────────────

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
    this.handleReset = this.handleReset.bind(this);
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.props.onError?.(error, info);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleReset() {
    this.setState({ hasError: false, error: null });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback != null) {
        return this.props.fallback;
      }
      return (
        <DefaultFallback error={this.state.error} onReset={this.handleReset} />
      );
    }

    return this.props.children;
  }
}
